import { useMemo, useRef, useState } from 'react';
import { Download, FileText, ImagePlus, Newspaper, Plus, Share2, Sparkles, Trash2 } from 'lucide-react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';

type Topic = 'past_match' | 'upcoming_match' | 'team' | 'player' | 'development' | 'sponsor' | 'community';
type Template = 'broadsheet' | 'match_review' | 'club_feature' | 'sponsor_spotlight';

type Draft = {
  topic: Topic;
  template: Template;
  subject: string;
  headlineEn: string;
  headlinePl: string;
  standfirstEn: string;
  standfirstPl: string;
  bodyEn: string;
  bodyPl: string;
  quoteEn: string;
  quotePl: string;
  media: string[];
  showSecondPage: boolean;
};

const topicLabels: Record<Topic, { en: string; pl: string }> = {
  past_match: { en: 'Previous Match', pl: 'Poprzedni mecz' },
  upcoming_match: { en: 'Upcoming Match', pl: 'Nadchodzący mecz' },
  team: { en: 'Team Story', pl: 'Historia drużyny' },
  player: { en: 'Player Feature', pl: 'Sylwetka zawodnika' },
  development: { en: 'Development', pl: 'Rozwój' },
  sponsor: { en: 'Sponsor Feature', pl: 'Sponsor' },
  community: { en: 'Club & Community', pl: 'Klub i społeczność' },
};

const templateLabels: Record<Template, string> = {
  broadsheet: 'Sunday Broadsheet',
  match_review: 'Match Review',
  club_feature: 'Club Feature',
  sponsor_spotlight: 'Sponsor Spotlight',
};

function dateLabel(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function defaultDraft(): Draft {
  return {
    topic: 'upcoming_match', template: 'broadsheet', subject: '',
    headlineEn: '', headlinePl: '', standfirstEn: '', standfirstPl: '', bodyEn: '', bodyPl: '', quoteEn: '', quotePl: '',
    media: [], showSecondPage: true,
  };
}

export function NewsCentre() {
  const { language } = useI18n();
  const { team, players, matches } = useData();
  const [draft, setDraft] = useState<Draft>(() => {
    try {
      const raw = localStorage.getItem('ecc_polonia_times_draft_v1');
      return raw ? { ...defaultDraft(), ...JSON.parse(raw) } : defaultDraft();
    } catch { return defaultDraft(); }
  });
  const [activeLanguage, setActiveLanguage] = useState<'en' | 'pl'>('en');
  const [status, setStatus] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const sorted = useMemo(() => [...matches].sort((a,b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime()), [matches]);
  const now = Date.now();
  const upcoming = sorted.find(m => new Date(m.match_date).getTime() >= now) || sorted[sorted.length - 1];
  const past = [...sorted].reverse().find(m => new Date(m.match_date).getTime() < now || m.status === 'completed');
  const club = team?.name || team?.club_name || 'Polonia Glasgow AFC';

  function save(next: Draft) {
    setDraft(next);
    try { localStorage.setItem('ecc_polonia_times_draft_v1', JSON.stringify(next)); } catch {}
  }

  function generate() {
    const subject = draft.subject.trim();
    let hEn = '', hPl = '', sEn = '', sPl = '', bEn = '', bPl = '', qEn = '', qPl = '';
    if (draft.topic === 'upcoming_match') {
      const m = upcoming;
      const opp = m?.opponent || subject || 'our next opponents';
      const date = dateLabel(m?.match_date);
      hEn = `${club} ready for ${opp} challenge`;
      hPl = `${club} gotowa na starcie z ${opp}`;
      sEn = `${date}${m?.competition ? ` · ${m.competition}` : ''} — preparations enter their final stage as Polonia turn attention to the next competitive test.`;
      sPl = `${date}${m?.competition ? ` · ${m.competition}` : ''} — przygotowania wchodzą w decydującą fazę, a Polonia koncentruje się na kolejnym ligowym wyzwaniu.`;
      bEn = `Polonia Glasgow AFC move into the next fixture with a clear focus on organisation, intensity and collective responsibility. The coaching staff will use the final sessions to confirm availability, refine the match plan and prepare the squad for the demands presented by ${opp}.\n\nThe final starting XI and substitutes will be confirmed through Elite Coach Cloud before matchday communication is released. Supporters can expect the usual Polonia matchday presentation across club channels.`;
      bPl = `Polonia Glasgow AFC przystępuje do kolejnego spotkania z wyraźnym naciskiem na organizację, intensywność i odpowiedzialność całego zespołu. Sztab wykorzysta ostatnie jednostki treningowe do potwierdzenia dostępności zawodników, dopracowania planu meczowego i przygotowania drużyny na wymagania rywala ${opp}.\n\nOstateczna jedenastka oraz ławka zostaną zatwierdzone w Elite Coach Cloud przed publikacją materiałów meczowych. Kibice mogą spodziewać się pełnej prezentacji Matchday w kanałach klubu.`;
      qEn = 'Preparation first. Clarity in the plan, intensity in the execution.';
      qPl = 'Najpierw przygotowanie. Jasny plan i intensywność w jego realizacji.';
    } else if (draft.topic === 'past_match') {
      const m = past;
      const opp = m?.opponent || subject || 'the opposition';
      const score = m && m.goals_for != null && m.goals_against != null ? `${m.goals_for}-${m.goals_against}` : 'the final whistle';
      hEn = `Polonia reflect on ${score} against ${opp}`;
      hPl = `Polonia podsumowuje ${score} z ${opp}`;
      sEn = `A structured review of the latest performance, key moments and the next areas of work.`;
      sPl = `Uporządkowane podsumowanie ostatniego występu, kluczowych momentów i kolejnych obszarów do pracy.`;
      bEn = `The latest match against ${opp} now moves from competition into analysis. The coaching staff will review the collective shape, decisive moments and individual contributions before translating the findings into the next training cycle.\n\nElite Coach Cloud provides one record for the match, player notes and follow-up work so that the post-match discussion becomes a concrete development plan.`;
      bPl = `Ostatni mecz z ${opp} przechodzi teraz z rywalizacji do analizy. Sztab przejrzy organizację zespołu, kluczowe momenty oraz indywidualny wkład zawodników, a następnie przełoży wnioski na kolejny cykl treningowy.\n\nElite Coach Cloud łączy zapis meczu, notatki o zawodnikach i dalsze zadania, dzięki czemu rozmowa pomeczowa staje się konkretnym planem rozwoju.`;
      qEn = 'Review the performance, keep the lessons, improve the next action.';
      qPl = 'Analizujemy występ, zachowujemy wnioski i poprawiamy kolejne działania.';
    } else if (draft.topic === 'player') {
      const p = players.find(p => p.name.toLowerCase().includes(subject.toLowerCase())) || players[0];
      const name = p?.name || subject || 'Polonia player';
      hEn = `${name}: inside the Polonia journey`;
      hPl = `${name}: droga w barwach Polonii`;
      sEn = `A closer look at the player, his role in the squad and the work behind matchday performances.`;
      sPl = `Bliższe spojrzenie na zawodnika, jego rolę w drużynie i pracę stojącą za występami meczowymi.`;
      bEn = `${name} represents the daily work that sits behind every matchday. From training detail to positional responsibility, the objective is consistent improvement and a stronger contribution to the team.\n\nThe coaching staff track development, form and individual priorities through Elite Coach Cloud, creating a clear reference point for future selection and training decisions.`;
      bPl = `${name} reprezentuje codzienną pracę stojącą za każdym dniem meczowym. Od detali treningowych po odpowiedzialność na pozycji — celem jest stały rozwój i coraz większy wkład w zespół.\n\nSztab śledzi rozwój, formę i indywidualne priorytety w Elite Coach Cloud, tworząc jasny punkt odniesienia dla kolejnych decyzji personalnych i treningowych.`;
      qEn = 'Progress is built in the work between matches.';
      qPl = 'Postęp buduje się w pracy pomiędzy meczami.';
    } else {
      const subjectName = subject || (draft.topic === 'sponsor' ? 'our valued partners' : club);
      hEn = draft.topic === 'sponsor' ? `${club} celebrates partnership with ${subjectName}` : `${club}: building beyond matchday`;
      hPl = draft.topic === 'sponsor' ? `${club} rozwija współpracę z ${subjectName}` : `${club}: rozwój wykraczający poza dzień meczowy`;
      sEn = `A Polonia Times feature on the people, partnerships and development work shaping the club.`;
      sPl = `Materiał Polonia Times o ludziach, partnerstwach i pracy rozwojowej budującej klub.`;
      bEn = `${club} continues to develop its identity through standards on the pitch and relationships away from it. ${subjectName} forms part of a wider story built around continuity, opportunity and a stronger environment for players and the club community.\n\nPolonia Times gives the club a consistent bilingual format to document that progress and share it professionally across digital channels.`;
      bPl = `${club} rozwija swoją tożsamość poprzez standardy na boisku i relacje poza nim. ${subjectName} jest częścią szerszej historii o ciągłości, szansach rozwoju i coraz lepszym środowisku dla zawodników oraz społeczności klubu.\n\nPolonia Times daje klubowi spójny, dwujęzyczny format do dokumentowania tego postępu i profesjonalnego publikowania go w kanałach cyfrowych.`;
      qEn = 'One club. One identity. A story worth telling well.';
      qPl = 'Jeden klub. Jedna tożsamość. Historia, którą warto opowiadać dobrze.';
    }
    save({ ...draft, headlineEn:hEn, headlinePl:hPl, standfirstEn:sEn, standfirstPl:sPl, bodyEn:bEn, bodyPl:bPl, quoteEn:qEn, quotePl:qPl });
    setStatus(language === 'pl' ? 'Dwujęzyczny artykuł został wygenerowany.' : 'Bilingual article generated.');
  }

  function addMedia(files: FileList | null) {
    if (!files) return;
    const urls = [...files].slice(0, 4).map(file => URL.createObjectURL(file));
    save({ ...draft, media: [...draft.media, ...urls].slice(0, 6) });
  }

  async function share() {
    const headline = activeLanguage === 'en' ? draft.headlineEn : draft.headlinePl;
    const body = activeLanguage === 'en' ? draft.standfirstEn : draft.standfirstPl;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Polonia Times · ${headline}`, text: `${headline}\n\n${body}`, url: window.location.href });
        setStatus(language === 'pl' ? 'Otwarto menu udostępniania.' : 'Share sheet opened.');
      } else {
        await navigator.clipboard.writeText(`${headline}\n\n${body}\n${window.location.href}`);
        setStatus(language === 'pl' ? 'Tekst skopiowany do schowka.' : 'Article text copied to clipboard.');
      }
    } catch {}
  }

  function printPages() {
    window.print();
  }

  const headline = activeLanguage === 'en' ? draft.headlineEn : draft.headlinePl;
  const standfirst = activeLanguage === 'en' ? draft.standfirstEn : draft.standfirstPl;
  const body = activeLanguage === 'en' ? draft.bodyEn : draft.bodyPl;
  const quote = activeLanguage === 'en' ? draft.quoteEn : draft.quotePl;

  return (
    <section className="news-centre">
      <div className="metal-card news-hero">
        <div>
          <div className="eyebrow"><Newspaper size={17}/> POLONIA TIMES</div>
          <h1>{language === 'pl' ? 'Studio prasowe' : 'Press Studio'}</h1>
          <p className="muted">{language === 'pl' ? 'Dwujęzyczne strony sportowe gotowe do publikacji, edycji i udostępniania.' : 'Bilingual sports pages ready to generate, edit, publish and share.'}</p>
        </div>
        <div className="news-hero-actions">
          <button className="gold-button" onClick={generate}><Sparkles size={17}/> {language === 'pl' ? 'Generuj EN + PL' : 'Generate EN + PL'}</button>
          <button className="steel-button" onClick={share}><Share2 size={17}/> {language === 'pl' ? 'Udostępnij' : 'Share'}</button>
          <button className="steel-button" onClick={printPages}><Download size={17}/> PDF</button>
        </div>
      </div>

      <div className="news-workspace">
        <aside className="metal-card news-controls">
          <h2>{language === 'pl' ? '1 · Temat' : '1 · Story type'}</h2>
          <div className="news-topic-grid">
            {(Object.keys(topicLabels) as Topic[]).map(topic => (
              <button key={topic} className={draft.topic === topic ? 'news-topic active' : 'news-topic'} onClick={() => save({ ...draft, topic })}>
                {topicLabels[topic][language]}
              </button>
            ))}
          </div>
          <label>{language === 'pl' ? 'Osoba / sponsor / temat (opcjonalnie)' : 'Person / sponsor / subject (optional)'}</label>
          <input value={draft.subject} onChange={e => save({ ...draft, subject:e.target.value })} placeholder={language === 'pl' ? 'np. Dominik Lichon / MVP Tyres' : 'e.g. Dominik Lichon / MVP Tyres'} />

          <h2>{language === 'pl' ? '2 · Styl strony' : '2 · Page style'}</h2>
          <div className="news-template-grid">
            {(Object.keys(templateLabels) as Template[]).map(template => (
              <button key={template} className={draft.template === template ? 'news-template active' : 'news-template'} onClick={() => save({ ...draft, template })}>
                <FileText size={15}/><span>{templateLabels[template]}</span>
              </button>
            ))}
          </div>

          <h2>{language === 'pl' ? '3 · Media' : '3 · Media'}</h2>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={e => addMedia(e.target.files)} />
          <button className="steel-button full" onClick={() => fileRef.current?.click()}><ImagePlus size={16}/> {language === 'pl' ? 'Dodaj zdjęcia / video' : 'Add photos / video'}</button>
          {draft.media.length > 0 && <div className="news-media-strip">{draft.media.map((url,i) => <div key={url} className="news-media-chip"><img src={url} alt=""/><button onClick={() => save({ ...draft, media:draft.media.filter((_,idx) => idx !== i) })}><Trash2 size={12}/></button></div>)}</div>}

          <label className="news-toggle"><input type="checkbox" checked={draft.showSecondPage} onChange={e => save({ ...draft, showSecondPage:e.target.checked })}/><span>{language === 'pl' ? 'Druga strona magazynu' : 'Second magazine page'}</span></label>
          {status && <div className="news-status">{status}</div>}
        </aside>

        <div className="news-editor-column">
          <div className="metal-card news-editor">
            <div className="news-language-tabs">
              <button className={activeLanguage==='en'?'active':''} onClick={() => setActiveLanguage('en')}>EN</button>
              <button className={activeLanguage==='pl'?'active':''} onClick={() => setActiveLanguage('pl')}>PL</button>
              <span>{language === 'pl' ? 'Obie wersje powstają razem' : 'Both versions are generated together'}</span>
            </div>
            <label>Headline</label>
            <textarea rows={2} value={headline} onChange={e => save(activeLanguage==='en'?{...draft,headlineEn:e.target.value}:{...draft,headlinePl:e.target.value})}/>
            <label>Standfirst</label>
            <textarea rows={3} value={standfirst} onChange={e => save(activeLanguage==='en'?{...draft,standfirstEn:e.target.value}:{...draft,standfirstPl:e.target.value})}/>
            <label>Article</label>
            <textarea rows={11} value={body} onChange={e => save(activeLanguage==='en'?{...draft,bodyEn:e.target.value}:{...draft,bodyPl:e.target.value})}/>
            <label>Pull quote</label>
            <textarea rows={2} value={quote} onChange={e => save(activeLanguage==='en'?{...draft,quoteEn:e.target.value}:{...draft,quotePl:e.target.value})}/>
          </div>

          <div className={`polonia-times-pages template-${draft.template}`}>
            <article className="polonia-times-page">
              <header className="times-masthead"><strong>POLONIA TIMES</strong><span>GLASGOW · FOOTBALL · 2026/27</span></header>
              <div className="times-kicker">{topicLabels[draft.topic][activeLanguage].toUpperCase()}</div>
              <h1>{headline || (activeLanguage==='pl'?'Tutaj pojawi się nagłówek':'Your headline will appear here')}</h1>
              <p className="times-standfirst">{standfirst}</p>
              <div className="times-media-grid">
                {draft.media[0] ? <img src={draft.media[0]} alt="Story media"/> : <div className="times-placeholder">POLONIA GLASGOW AFC</div>}
                {draft.media[1] && <img src={draft.media[1]} alt="Story media"/>}
              </div>
              <div className="times-copy">{body.split('\n').map((p,i) => <p key={i}>{p}</p>)}</div>
              {quote && <blockquote>“{quote}”</blockquote>}
              <footer><span>POLONIA GLASGOW AFC</span><span>ELITE COACH CLOUD PRESS STUDIO</span></footer>
            </article>

            {draft.showSecondPage && <article className="polonia-times-page second-page">
              <header className="times-masthead"><strong>POLONIA TIMES</strong><span>{activeLanguage==='pl'?'STRONA 2 · PERSPEKTYWA KLUBU':'PAGE 2 · CLUB PERSPECTIVE'}</span></header>
              <div className="times-secondary-layout">
                <div>
                  <div className="times-kicker">{activeLanguage==='pl'?'WIĘCEJ Z KLUBU':'MORE FROM THE CLUB'}</div>
                  <h2>{activeLanguage==='pl'?'Obraz, kontekst i kolejny krok':'The picture, the context and what comes next'}</h2>
                  <p>{standfirst}</p>
                  <blockquote>“{quote || (activeLanguage==='pl'?'Polonia Times — profesjonalny głos klubu.':'Polonia Times — the professional voice of the club.')}”</blockquote>
                </div>
                <div className="times-media-stack">
                  {(draft.media.slice(1,4).length ? draft.media.slice(1,4) : [draft.media[0]]).filter(Boolean).map(url => <img key={url} src={url} alt="Story media"/>)}
                  {!draft.media.length && <div className="times-placeholder tall">POLONIA<br/>TIMES</div>}
                </div>
              </div>
              <footer><span>BILINGUAL · EN / PL</span><span>POLONIA GLASGOW AFC</span></footer>
            </article>}
          </div>
        </div>
      </div>
    </section>
  );
}
